import { useState } from 'react'
import { X, Clock } from 'lucide-react'
import { SERIES_TYPE_LABELS, type SeriesType, type Series } from '@/types/series'
import { SeriesTypeSelector } from './SeriesTypeSelector'
import { useCreateSeries, useUpdateSeries } from '@/hooks/useSeries'
import { useAuth } from '@/lib/auth'
import { useLabels, singular } from '@/lib/terminology'

interface CreateSeriesModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
  series?: Series | null
}

export function CreateSeriesModal({
  isOpen,
  onClose,
  onSuccess,
  series,
}: CreateSeriesModalProps) {
  // Generate a key to force form reset when modal opens
  const modalKey = isOpen ? `open-${series?.id || 'new'}` : 'closed'

  if (!isOpen) return null

  return (
    <CreateSeriesModalContent
      key={modalKey}
      onClose={onClose}
      onSuccess={onSuccess}
      series={series}
    />
  )
}

interface CreateSeriesModalContentProps {
  onClose: () => void
  onSuccess?: () => void
  series?: Series | null
}

function CreateSeriesModalContent({
  onClose,
  onSuccess,
  series,
}: CreateSeriesModalContentProps) {
  const { membership } = useAuth()
  const labels = useLabels()
  const createSeries = useCreateSeries()
  const updateSeries = useUpdateSeries()

  const isEditMode = !!series

  const [name, setName] = useState(series?.name || '')
  const [description, setDescription] = useState(series?.description || '')
  const [seriesType, setSeriesType] = useState<SeriesType>(series?.series_type || 'podcast')
  const [productionLeadDays, setProductionLeadDays] = useState(series?.production_lead_days ?? 7)
  const [recordingToReleaseDays, setRecordingToReleaseDays] = useState(series?.recording_to_release_days ?? 7)

  const [error, setError] = useState<string | null>(null)

  const isPending = createSeries.isPending || updateSeries.isPending

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!name.trim()) {
      return
    }

    try {
      if (isEditMode && series) {
        await updateSeries.mutateAsync({
          id: series.id,
          name: name.trim(),
          description: description.trim() || undefined,
          series_type: seriesType,
          production_lead_days: productionLeadDays,
          recording_to_release_days: recordingToReleaseDays,
        })
      } else {
        if (!membership?.team_id) {
          setError(`Unable to create ${singular(labels.collections).toLowerCase()}. Please refresh the page and try again.`)
          return
        }

        await createSeries.mutateAsync({
          team_id: membership.team_id,
          name: name.trim(),
          description: description.trim() || undefined,
          series_type: seriesType,
          production_lead_days: productionLeadDays,
          recording_to_release_days: recordingToReleaseDays,
        })
      }

      onSuccess?.()
      onClose()
    } catch (err) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} series:`, err)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-md mx-4 bg-white dark:bg-zinc-900 rounded-xl shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200 dark:border-zinc-800">
          <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
            {isEditMode ? `Edit ${singular(labels.collections)}` : `Create New ${singular(labels.collections)}`}
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
          >
            <X className="w-5 h-5 text-zinc-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 space-y-4">
            {/* Series Type */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                Type
              </label>
              <SeriesTypeSelector
                value={seriesType}
                onChange={setSeriesType}
                disabled={isPending}
              />
            </div>

            {/* Name */}
            <div>
              <label
                htmlFor="series-name"
                className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1"
              >
                Name
              </label>
              <input
                id="series-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="My Awesome Podcast"
                disabled={isPending}
                className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label
                htmlFor="series-description"
                className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1"
              >
                Description{' '}
                <span className="text-zinc-400 dark:text-zinc-500">(optional)</span>
              </label>
              <textarea
                id="series-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="A short description of your series..."
                rows={3}
                disabled={isPending}
                className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 resize-none"
              />
            </div>

            {/* Timing Settings */}
            <div className="pt-2 border-t border-zinc-200 dark:border-zinc-700">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-4 h-4 text-zinc-500" />
                <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                  Production Timeline
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Production Lead Days */}
                <div>
                  <label
                    htmlFor="production-lead-days"
                    className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1"
                  >
                    Lead Time
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      id="production-lead-days"
                      type="number"
                      min={1}
                      max={90}
                      value={productionLeadDays}
                      onChange={(e) => setProductionLeadDays(Math.max(1, parseInt(e.target.value) || 1))}
                      disabled={isPending}
                      className="w-20 px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
                    />
                    <span className="text-sm text-zinc-500 dark:text-zinc-400">days</span>
                  </div>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
                    Schedule episodes this far ahead
                  </p>
                </div>

                {/* Recording to Release Days */}
                <div>
                  <label
                    htmlFor="recording-to-release-days"
                    className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1"
                  >
                    Production Time
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      id="recording-to-release-days"
                      type="number"
                      min={0}
                      max={90}
                      value={recordingToReleaseDays}
                      onChange={(e) => setRecordingToReleaseDays(Math.max(0, parseInt(e.target.value) || 0))}
                      disabled={isPending}
                      className="w-20 px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
                    />
                    <span className="text-sm text-zinc-500 dark:text-zinc-400">days</span>
                  </div>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
                    Time from recording to release
                  </p>
                </div>
              </div>
            </div>

            {(error || createSeries.isError || updateSeries.isError) && (
              <p className="text-sm text-rose-600 dark:text-rose-400">
                {error || `Failed to ${isEditMode ? 'update' : 'create'} ${singular(labels.collections).toLowerCase()}. Please try again.`}
              </p>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-zinc-200 dark:border-zinc-800">
            <button
              type="button"
              onClick={onClose}
              disabled={isPending}
              className="px-4 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!name.trim() || isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isPending
                ? (isEditMode ? 'Saving...' : 'Creating...')
                : (isEditMode ? 'Save Changes' : `Create ${SERIES_TYPE_LABELS[seriesType]}`)}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
